import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

// Activity week strip and day view (card #4070).
//
// Sits on the page wrapper, outside the field_activity Turbo Frame, so it
// survives every week or day change. It does three things:
//
//   1. Date picker: submits the picker's form when a date is chosen.
//   2. Swipe: a horizontal swipe on the strip follows the previous / next
//      week link. Vertical drags are ignored so page scrolling is untouched.
//   3. List/Map and the order switch: kept in the query string like the date and
//      filters. Search carries every other param from the address bar.
//   4. Online only: the week and day views are fetched live. If the request
//      fails (no signal, server error) the frame is hidden and a plain message
//      with a retry is shown instead of a blank page.
export default class extends Controller {
  static targets = ["frame", "error", "picker", "previous", "next", "mapShell"]
  static values = { frameId: String }

  static SWIPE_MIN_PX = 60

  connect() {
    this.failedUrl = null
    this.element.addEventListener("turbo:fetch-request-error", this.requestFailed)
    this.element.addEventListener("turbo:frame-missing", this.frameMissing)
    this.element.addEventListener("turbo:frame-load", this.loaded)
  }

  disconnect() {
    this.element.removeEventListener("turbo:fetch-request-error", this.requestFailed)
    this.element.removeEventListener("turbo:frame-missing", this.frameMissing)
    this.element.removeEventListener("turbo:frame-load", this.loaded)
  }

  // Date picker

  openPicker() {
    try {
      this.pickerTarget.showPicker?.()
    } catch (_error) {
      // Not allowed without a user gesture, or unsupported: the native input still works.
    }
  }

  pick() {
    if (!this.pickerTarget.value) return
    this.pickerTarget.form.requestSubmit()
  }

  // Search form: carry the rest of the query string (date, week, filters, view, order)
  // from the address bar, which the Turbo Frame keeps current (data-turbo-action="advance").
  carryParams(event) {
    const form = event.target
    form.querySelectorAll("input[data-carried]").forEach(input => input.remove())
    const params = new URL(window.location.href).searchParams
    params.forEach((value, name) => {
      if (name === "q" || name === "page" || value === "") return
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = name
      input.value = value
      input.dataset.carried = ""
      form.appendChild(input)
    })
  }

  // List / Map toggle. The map needs Mapbox, loaded only for view=map, so switching is a full page load.
  showList() { this.switchView(null) }
  showMap()  { this.switchView("map") }

  switchView(view) {
    const url = new URL(window.location.href)
    url.searchParams.delete("page")
    if (view) {
      url.searchParams.set("view", view)
    } else {
      url.searchParams.delete("view")
      url.searchParams.delete("order")
    }
    window.location.assign(url.toString())
  }

  // "Show order visited" switch on the map: re-render the frame with order in the URL.
  toggleOrder(event) {
    const url = new URL(window.location.href)
    const turningOn = event.currentTarget.getAttribute("aria-checked") !== "true"
    turningOn ? url.searchParams.set("order", "1") : url.searchParams.delete("order")
    Turbo.visit(url.toString(), { frame: this.frameIdValue, action: "advance" })
  }

  // Swipe

  swipeStart(event) {
    const touch = event.changedTouches[0]
    this.swipeOrigin = { x: touch.clientX, y: touch.clientY }
  }

  swipeEnd(event) {
    if (!this.swipeOrigin) return

    const touch = event.changedTouches[0]
    const dx = touch.clientX - this.swipeOrigin.x
    const dy = touch.clientY - this.swipeOrigin.y
    this.swipeOrigin = null

    const horizontal = Math.abs(dx) >= this.constructor.SWIPE_MIN_PX && Math.abs(dx) > Math.abs(dy) * 1.5
    if (!horizontal) return

    // Swipe left reveals the next week; swipe right goes back.
    if (dx < 0 && this.hasNextTarget) this.nextTarget.click()
    if (dx > 0 && this.hasPreviousTarget) this.previousTarget.click()
  }

  // Online only

  requestFailed = (event) => {
    const request = event.detail?.request
    this.showError((request?.location || request?.url)?.toString())
  }

  frameMissing = (event) => {
    // A server error page has no matching frame. Show the message rather than
    // letting Turbo replace the whole page with the error.
    event.preventDefault()
    this.showError(event.target?.src)
  }

  loaded = () => {
    this.errorTarget.classList.add("hidden")
    this.frameTarget.classList.remove("hidden")
    this.syncMapShell()
  }

  // The map shell lives outside the frame. Show it only when the day in the frame has pins to plot.
  syncMapShell() {
    if (!this.hasMapShellTarget) return
    const hasPins = !!this.frameTarget.querySelector("#field_activity-map-places")
    this.mapShellTarget.classList.toggle("hidden", !hasPins)
    if (hasPins) window.dispatchEvent(new CustomEvent("field-places-map:resize"))
  }

  retry() {
    const url = this.failedUrl
    this.errorTarget.classList.add("hidden")
    this.frameTarget.classList.remove("hidden")
    this.syncMapShell()
    if (url) Turbo.visit(url, { frame: this.frameIdValue, action: "advance" })
  }

  showError(url) {
    this.failedUrl = url || this.failedUrl || window.location.href
    this.frameTarget.classList.add("hidden")
    if (this.hasMapShellTarget) this.mapShellTarget.classList.add("hidden")
    this.errorTarget.classList.remove("hidden")
  }
}
